<template>
  <div class="app-results">
    <div class="app-results__bar">
      <div class="app-results__bar__number">
        {{ ((resultsNumber | defaultView) + ' Results') | t }}
      </div>
      <multiselect
        id="filter-location"
        v-model="sortAs"
        :options="sortOptions"
        :clear-on-select="false"
        :preserve-search="true"
        select-label=""
        deselect-label=""
        selected-label=""
        :searchable="false"
        placeholder="Show all"
        open-direction="bottom"
        label="filter"
        track-by="id"
      >
        <template slot="selection">
          <select
            ref="input"
            v-model="sortAs"
            name="sort-select"
            class="multiselect__native-select"
          >
            <option v-for="option in sortOptions" :key="option" name="sort-select" :value="option">
              {{ option.filter }}
            </option>
          </select>
        </template>
      </multiselect>
    </div>

    <div class="app-results__data">
      <article v-if="!results.length && !showPreloader" class="result-item result-item--no-results">
        <span class="material-icons-outlined">error_outline</span>
        <p>
          Sorry, there was no activity found.
          <a
            :href="noResultUrl"
            title='"Alert when activities get posted" | t'
            >Click here</a
          >
          to get an alert when activities get posted.
        </p>
      </article>
      <article v-if="showPreloader" class="result-item result-item--no-results">
        <AppPreloader />
      </article>
      <template v-if="!showPreloader">
        <article v-for="item in results" :key="item.nid" class="result-item">
          <div class="result-item__head">
            <button class="result-item__head__title" @click="showModal(item)">
              {{ item.name }}
            </button>
            <div v-if="item.price.price" class="result-item__head__price">
              <mark>{{ item.price.price | defaultView }}</mark>
              <span v-if="item.price.unit_of_measure">
                / {{ item.price.unit_of_measure.toLowerCase() }}
              </span>
            </div>
          </div>
          <div class="result-item__body">
            <div class="result-item__body__col">
              <div class="label">{{ 'Date:' | t }}</div>
              <div class="data">
                <p>{{ item.dates | defaultView }}</p>
                <p v-for="schedule in item.schedule" :key="schedule.days">{{ schedule.days }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Time:' | t }}</div>
              <div class="data">
                <p v-for="schedule in item.schedule" :key="schedule.time">{{ schedule.time }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Location:' | t }}</div>
              <div class="data">
                <p>{{ item.location | defaultView }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Sport:' | t }}</div>
              <div class="data">
                <p>{{ item.sport | defaultView }}</p>
              </div>
            </div>
            <div class="result-item__body__col">
              <div class="label">{{ 'Ages:' | t }}</div>
              <div class="data">
                <p>{{ item.ages | defaultView }}</p>
              </div>
            </div>
            <div class="result-item__body__col result-item__body__col--btn">
              <a class="app-btn" :href="item.link" @click="GtmRegister(item.name)">{{
                'Register Now' | t
              }}</a>
            </div>
          </div>
        </article>
      </template>
    </div>

    <CalendarModal
      v-if="results.length"
      :modal-data="modalData || results[0]"
      :modal-show="modalShowing"
      @close-modal="modalShowing = false"
    />
  </div>
</template>

<script>
import CalendarModal from './CalendarModal'
import AppPreloader from './AppPreloader'

export default {
  components: {
    CalendarModal,
    AppPreloader
  },
  filters: {
    defaultView(item) {
      return item ? item : '-'
    }
  },
  props: {
    resultsNumber: {
      type: Number,
      default: 0
    },
    results: {
      type: Array,
      default: null
    },
    sortOptions: {
      type: Array,
      default: null
    },
    initialSort: {
      type: Object,
      default: null
    },
    showPreloader: {
      type: Boolean,
      default: true
    },
    noResultUrl: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      sortAs: JSON.parse(JSON.stringify(this.initialSort)),
      modalData: null,
      modalShowing: false,
      initialQueryParsed: false
    }
  },
  watch: {
    sortAs: {
      deep: true,
      handler() {
        this.$emit('sort-changed', this.sortAs)
      }
    }
  },
  methods: {
    showModal(row) {
      this.modalData = row
      this.modalShowing = true
    },
    GtmRegister(name) {
      if (name)
        window.dataLayer.push({
          event: 'sns-activenet-widget',
          'sns-widget-category': 'spookynook-activenet-calendar',
          'sns-widget-action': 'register',
          'sns-widget-label': name
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.app-results {
  margin-top: 16px;
  font-family: $font-main;

  @include media-breakpoint-up(lg) {
    margin-top: 0;
  }

  &__bar {
    display: flex;
    flex-direction: column-reverse;

    @include media-breakpoint-up(lg) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      background: $color-gray-dark;
      padding: 8px 20px 7px;
    }

    &__number {
      background: $color-gray-dark;
      font-family: $font-secondary;
      font-size: rem(18);
      text-transform: uppercase;
      color: $color-white;
      padding: 10px 15px 9px;
      margin-top: 18px;

      @include media-breakpoint-up(lg) {
        margin-top: 0;
        padding: 0;
      }
    }

    .multiselect {
      flex-basis: auto;

      @include media-breakpoint-up(lg) {
        flex-basis: 236px;
      }
    }
  }
}

.result-item {
  padding: 17px 15px 16px;
  color: $color-black;

  &:nth-child(2n) {
    background: $color-gray-bg;
  }

  &--no-results {
    height: 100px;
    background: $color-gray-light;
    font-weight: bold;
    font-size: rem(14);
    line-height: rem(19);
    color: $color-black;
    display: flex;
    align-items: center;

    p {
      margin: 0;
    }

    span {
      font-size: rem(34);
      color: $color-green;
      display: inline-block;
      font-weight: normal;
      margin-right: 15px;
    }

    a {
      color: $color-green;
    }
  }

  &__head {
    @include media-breakpoint-up(lg) {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &__title {
      border: 0;
      box-shadow: none;
      color: $color-blue;
      text-transform: uppercase;
      font-family: $font-secondary;
      margin-bottom: 16px;
      font-size: rem(20);
      background: transparent;
      padding: 0;
      transition: color 0.2s ease-in-out;

      &:hover {
        color: $color-green;
        cursor: pointer;
      }
    }

    &__price {
      font-size: rem(12);
      line-height: rem(17);

      mark {
        font-family: $font-secondary;
        background: transparent;
        font-size: rem(18);
        padding: 0;
      }
    }
  }

  &__body {
    font-size: rem(14);
    display: grid;
    grid-template-columns: 1fr 1fr;

    @include media-breakpoint-up(lg) {
      grid-template: 1fr / 1fr 1fr 1fr 1fr 1fr auto;
    }

    &__col {
      margin-top: 16px;

      @include media-breakpoint-up(lg) {
        margin-top: 0;
      }

      .label {
        font-weight: bold;
        margin-bottom: 10px;
      }

      p {
        margin-bottom: 5px;
      }

      &--btn {
        grid-column: 1 / 3;
        grid-row: 4;
        margin-top: 26px;

        @include media-breakpoint-up(lg) {
          grid-column: auto;
          grid-row: auto;
          margin-top: 16px;
          justify-self: end;
        }
      }
    }
  }
}
</style>
